import React, { Suspense, lazy, useEffect } from "react";
import { Route, Routes, BrowserRouter, Navigate } from "react-router-dom";
import "./Css/App.css";
import PrivateRoute from "./PrivateRoute";
import ErrorBoundary from "./ErrorBoundary";
import { preloadSuperDashboard } from "./routePreloaders";

import ClassicLandingPage from "./LandingPage/ClassicLandingPage";

const StudentTable2 = lazy(() => import("./StudentTable2"));
const AddStudent = lazy(() => import("./AddStudent"));
const AddNewStaff = lazy(() => import("./AddNewStaff"));
const StudentBulkImport = lazy(() => import("./StudentBulkImport"));
const StaffBulkImport = lazy(() => import("./StaffBulkImport"));
const Class = lazy(() => import("./Teacher-app/Class"));
const InputData = lazy(() => import("./InputData"));
const SelectClassView = lazy(() => import("./Teacher-app/SelectClassView"));
const TeacherHome = lazy(() => import("./Teacher-app/TeacherHome"));
const TeacherAttendancePage = lazy(() => import("./TeacherAttendancePage"));
const TeacherSelfAttendance = lazy(() => import("./Teacher-app/TeacherSelfAttendance"));
const MySalary = lazy(() => import("./Teacher-app/MySalary"));
const AddClass = lazy(() => import("./AddClass"));
const MasterUserTasks = lazy(() => import("./MasterUserTasks"));
const SuperUserHomeDashboard = lazy(preloadSuperDashboard);
const ThankyouPage = lazy(() => import("./ThankyouPage"));
const AddNewSyllabus = lazy(() => import("./AddNewSyllabus"));
const LiveSyllabus = lazy(() => import("./Teacher-app/LiveSyllabus"));
const FeeManagementRouter = lazy(() => import("./FeeManagement/FeeManagementRouter"));
const SalaryStatus = lazy(() => import("./SalaryStatus"));
const ForgotPassword = lazy(() => import("./ForgotPassword"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const StaffBio = lazy(() => import("./StaffBio"));
const ClasswiseStatus = lazy(() => import("./ClasswiseStatus"));
const LandingPage = lazy(() => import("./LandingPage/LandingPage"));
const DownloadRecords = lazy(() => import("./DownloadRecords"));
const UserManagement = lazy(() => import("./UserManagement"));
const StaffPermissionManager = lazy(() => import("./StaffPermissionManager"));
const PaymentSettingsPage = lazy(() => import("./PaymentSettings").then(m => ({ default: m.PaymentSettingsPage })));
const PrivacyPolicy = lazy(() => import("./PrivacyPolicy"));
const CertificateGenerator = lazy(() => import("./CertificateGenerator"));
const DocumentTemplateEditor = lazy(() => import("./DocumentTemplateEditor"));
const SocialMediaPage = lazy(() => import("./SocialMedia/SocialMediaPage"));
const UserProfile = lazy(() => import("./UserProfile"));
const AuditLogs = lazy(() => import("./AuditLogs"));
const SchoolPresentationMode = lazy(() => import("./SchoolPresentation/SchoolPresentationMode"));
const PromoVideoPage = lazy(() => import("./PromoVideo/PromoVideoPage"));
const ScanAdmission = lazy(() => import("./ScanAdmission"));
const ComplianceDashboard = lazy(() => import("./Compliance/ComplianceDashboard"));
const PublicPocsoReport = lazy(() => import("./Compliance/Pocso/PublicPocsoReport"));
const PublicPaymentPage = lazy(() => import("./PublicPay/PublicPaymentPage"));
const AdmissionsCRM = lazy(() => import("./Admissions/AdmissionsCRM"));
const HrManagement = lazy(() => import("./HrManagement"));
const OperationsErpHub = lazy(() => import("./Operations/OperationsErpHub"));
const AssistantSettings = lazy(() => import("./AssistantSettings"));

const RouteFallback = () => (
  <div
    role="status"
    aria-label="Loading page"
    style={{
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      background: "#f8fafc",
      color: "#27697b",
      font: "600 15px system-ui, sans-serif",
    }}
  >
    Loading…
  </div>
);

// Role utilities are available in ../utils/roleUtils.js
// Components can import these for role-based conditional rendering:
// import { hasRoleWithPriority, getHighestPriorityRoleFromStorage, hasMinimumRolePriority } from "../utils/roleUtils";


function App() {
  useEffect(() => {
    // Ensure modern UI class is applied to body //
    document.body.className = document.body.className.replace(/(^|\s)(classic-ui|modern-ui)(\s|$)/g, ' ');
    document.body.classList.add('modern-ui');
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public Routes (No Authentication Needed) */}
          <Route path="/" element={<ClassicLandingPage />} />
          <Route path="/hybrid" element={<LandingPage />} />
          <Route path="/experience-3d" element={<LandingPage mode="full" />} />
          <Route path="/entrypage" element={<ClassicLandingPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/auth/reset-password/:token"
            element={<ResetPassword />}
          />
          <Route path="/thankyou" element={<ThankyouPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* <Route path="/presentation" element={<PresentationMode />} /> */}
          <Route path="/school-presentation" element={<SchoolPresentationMode />} />
          <Route path="/promo-video" element={<PromoVideoPage />} />
          {/* <Route path="/test" element={<TestApp />} /> */}
          <Route path="/pocso-report" element={<PublicPocsoReport />} />
          <Route path="/pay/:token" element={<PublicPaymentPage />} />

          {/* Protected Routes (Require Authentication) */}
          {/* Note: PrivateRoute now has access to enhanced role checking via roleUtils */}
          <Route element={<PrivateRoute />}>
            <Route path="/t" element={<StudentTable2 />} />
            <Route path="/input" element={<InputData />} />
            <Route path="/class" element={<Class />} />
            <Route path="/master" element={<MasterUserTasks />} />
            <Route path="/super" element={<SuperUserHomeDashboard />} />
            <Route path="/selectclass" element={<SelectClassView />} />
            <Route path="/teacherhome" element={<TeacherHome />} />
            <Route path="/admintasks" element={<Navigate to="/super" replace />} />
            <Route path="/admin-dashboard" element={<Navigate to="/super" replace />} />
            <Route path="/teacher-attendance" element={<TeacherAttendancePage />} />
            <Route path="/addstudent" element={<AddStudent />} />
            <Route path="/addstaff" element={<AddNewStaff />} />
            <Route path="/student-import" element={<StudentBulkImport />} />
            <Route path="/staff-import" element={<StaffBulkImport />} />
            <Route path="/addclass" element={<AddClass />} />
            <Route path="/add-new-syllabus" element={<AddNewSyllabus />} />
            <Route path="/livesyllabus" element={<LiveSyllabus />} />
            <Route path="/feeStatus" element={<Navigate to="/fee-management" replace />} />
            <Route path="/fee-management/*" element={<FeeManagementRouter />} />
            <Route path="/unified-fee-management" element={<Navigate to="/fee-management" replace />} />
            <Route path="/parent-fee-management" element={<Navigate to="/fee-management/collect/parent" replace />} />
            <Route path="/master-fee-management" element={<Navigate to="/fee-management/structure" replace />} />
            <Route path="/costManagement" element={<Navigate to="/fee-management/structure" replace />} />
            <Route path="/salaryStatus" element={<SalaryStatus />} />
            <Route path="/staffbio" element={<StaffBio />} />
            <Route path="/downloadrecords" element={<DownloadRecords />} />
            <Route path="/usermanagement" element={<UserManagement />} />
            <Route path="/staff-permissions" element={<StaffPermissionManager />} />
            <Route path="/payment-settings" element={<PaymentSettingsPage />} />
            <Route path="/classwisestatus" element={<ClasswiseStatus />} />
            <Route path="/certificates" element={<CertificateGenerator />} />
            <Route path="/document-templates" element={<DocumentTemplateEditor />} />
            <Route path="/social" element={<SocialMediaPage />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/assistant-settings" element={<AssistantSettings />} />
            <Route path="/my-attendance" element={<TeacherSelfAttendance />} />
            <Route path="/my-salary" element={<MySalary />} />
            <Route path="/user-logs" element={<AuditLogs />} />
            <Route path="/scan-admission" element={<ScanAdmission />} />
            <Route path="/admissions" element={<AdmissionsCRM />} />
            <Route path="/hr-management" element={<HrManagement />} />
            <Route path="/operations" element={<OperationsErpHub />} />
            <Route path="/inventory" element={<OperationsErpHub initialModule="inventory" />} />
            <Route path="/school-events" element={<OperationsErpHub initialModule="events" />} />
            <Route path="/erp-extensions" element={<OperationsErpHub initialModule="transport-vehicles" />} />
            <Route path="/compliance/*" element={<ComplianceDashboard />} />
            <Route path="/teacher/observations" element={<Navigate to="/compliance/hpc?view=teacher-assessment" replace />} />
          </Route>

          {/* Catch-All Route (404 Page) */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
